import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import type { CampaignMetrics } from '../../types/campaign';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface CampaignMetricsChartsProps {
  metrics: CampaignMetrics;
}

export const CampaignMetricsCharts: React.FC<CampaignMetricsChartsProps> = ({ metrics }) => {
  // Delivery Funnel Data
  const funnelData = {
    labels: ['Total', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Replied'],
    datasets: [
      {
        label: 'Campaign Funnel',
        data: [
          metrics.total_contacts,
          metrics.sent,
          metrics.delivered,
          metrics.opened,
          metrics.clicked,
          metrics.replied,
        ],
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',   // Indigo - Total
          'rgba(59, 130, 246, 0.8)',   // Blue - Sent
          'rgba(34, 197, 94, 0.8)',    // Green - Delivered
          'rgba(168, 85, 247, 0.8)',   // Purple - Opened
          'rgba(236, 72, 153, 0.8)',   // Pink - Clicked
          'rgba(251, 146, 60, 0.8)',   // Orange - Replied
        ],
        borderColor: [
          'rgb(99, 102, 241)',
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
          'rgb(236, 72, 153)',
          'rgb(251, 146, 60)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const funnelOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Campaign Delivery Funnel',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.x;
            const total = metrics.total_contacts;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        max: metrics.total_contacts,
      },
    },
  };

  // Status Distribution Data
  const statusData = {
    labels: ['Pending', 'Sent', 'Delivered', 'Opened', 'Clicked', 'Bounced', 'Unsubscribed'],
    datasets: [
      {
        label: 'Contact Status',
        data: [
          metrics.pending,
          metrics.sent - metrics.delivered,
          metrics.delivered - metrics.opened,
          metrics.opened - metrics.clicked,
          metrics.clicked,
          metrics.bounced,
          metrics.unsubscribed,
        ],
        backgroundColor: [
          'rgba(156, 163, 175, 0.8)',  // Gray - Pending
          'rgba(59, 130, 246, 0.8)',   // Blue - Sent
          'rgba(34, 197, 94, 0.8)',    // Green - Delivered
          'rgba(168, 85, 247, 0.8)',   // Purple - Opened
          'rgba(236, 72, 153, 0.8)',   // Pink - Clicked
          'rgba(239, 68, 68, 0.8)',    // Red - Bounced
          'rgba(251, 146, 60, 0.8)',   // Orange - Unsubscribed
        ],
        borderColor: [
          'rgb(156, 163, 175)',
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
          'rgb(236, 72, 153)',
          'rgb(239, 68, 68)',
          'rgb(251, 146, 60)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
      title: {
        display: true,
        text: 'Contact Status Distribution',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const value = context.parsed;
            const total = metrics.total_contacts;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
  };

  // Format percentage
  const formatPercentage = (value: number) => {
    return `${Math.round(value * 100)}%`;
  };

  // Get performance indicator color
  const getPerformanceColor = (rate: number, type: 'good' | 'bad') => {
    if (type === 'good') {
      // Higher is better (delivery, open, click rates)
      if (rate >= 0.7) return 'bg-green-500';
      if (rate >= 0.4) return 'bg-yellow-500';
      return 'bg-red-500';
    } else {
      // Lower is better (bounce, unsubscribe rates)
      if (rate <= 0.05) return 'bg-green-500';
      if (rate <= 0.15) return 'bg-yellow-500';
      return 'bg-red-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Performance Metrics Cards */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Performance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Delivery Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Delivery Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.delivery_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.delivery_rate, 'good')}`}
                style={{ width: formatPercentage(metrics.delivery_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.delivered} of {metrics.sent} delivered</p>
          </div>

          {/* Open Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Open Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.open_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.open_rate, 'good')}`}
                style={{ width: formatPercentage(metrics.open_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.opened} of {metrics.sent} opened</p>
          </div>

          {/* Click Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Click Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.click_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.click_rate, 'good')}`}
                style={{ width: formatPercentage(metrics.click_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.clicked} of {metrics.sent} clicked</p>
          </div>

          {/* Reply Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Reply Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.reply_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.reply_rate, 'good')}`}
                style={{ width: formatPercentage(metrics.reply_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.replied} of {metrics.sent} replied</p>
          </div>

          {/* Bounce Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Bounce Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.bounce_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.bounce_rate, 'bad')}`}
                style={{ width: formatPercentage(metrics.bounce_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.bounced} of {metrics.sent} bounced</p>
          </div>

          {/* Unsubscribe Rate */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Unsubscribe Rate</span>
              <span className="text-lg font-semibold text-gray-900">{formatPercentage(metrics.unsubscribe_rate)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.unsubscribe_rate, 'bad')}`}
                style={{ width: formatPercentage(metrics.unsubscribe_rate) }}
              ></div>
            </div>
            <p className="mt-1 text-xs text-gray-500">{metrics.unsubscribed} of {metrics.sent} unsubscribed</p>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Delivery Funnel Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="h-80">
            <Bar data={funnelData} options={funnelOptions} />
          </div>
        </div>

        {/* Status Distribution Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="h-80">
            <Doughnut data={statusData} options={statusOptions} />
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Campaign Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{metrics.total_contacts}</div>
            <div className="text-sm text-gray-500 mt-1">Total Contacts</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-900">{metrics.sent}</div>
            <div className="text-sm text-blue-700 mt-1">Sent</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-900">{metrics.delivered}</div>
            <div className="text-sm text-green-700 mt-1">Delivered</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-900">{metrics.opened}</div>
            <div className="text-sm text-purple-700 mt-1">Opened</div>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Engagement Breakdown</h3>
        <div className="space-y-4">
          {/* Clicked */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Clicked</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.clicked} ({formatPercentage(metrics.click_rate)})
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-pink-500 h-2 rounded-full transition-all duration-500"
                style={{ width: formatPercentage(metrics.click_rate) }}
              ></div>
            </div>
          </div>

          {/* Replied */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Replied</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.replied} ({formatPercentage(metrics.reply_rate)})
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                style={{ width: formatPercentage(metrics.reply_rate) }}
              ></div>
            </div>
          </div>

          {/* Bounced */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Bounced</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.bounced} ({formatPercentage(metrics.bounce_rate)})
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.bounce_rate, 'bad')}`}
                style={{ width: formatPercentage(metrics.bounce_rate) }}
              ></div>
            </div>
          </div>

          {/* Unsubscribed */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">Unsubscribed</span>
              <span className="text-sm font-semibold text-gray-900">
                {metrics.unsubscribed} ({formatPercentage(metrics.unsubscribe_rate)})
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${getPerformanceColor(metrics.unsubscribe_rate, 'bad')}`}
                style={{ width: formatPercentage(metrics.unsubscribe_rate) }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Overall Engagement */}
          <div className="text-center p-6 border-2 border-gray-200 rounded-lg">
            <div className="text-3xl font-bold text-indigo-600">
              {formatPercentage((metrics.opened + metrics.clicked + metrics.replied) / (metrics.sent || 1))}
            </div>
            <div className="text-sm text-gray-600 mt-2">Overall Engagement</div>
            <div className="text-xs text-gray-500 mt-1">
              {metrics.opened + metrics.clicked + metrics.replied} engaged contacts
            </div>
          </div>

          {/* Success Rate */}
          <div className="text-center p-6 border-2 border-gray-200 rounded-lg">
            <div className="text-3xl font-bold text-green-600">
              {formatPercentage(metrics.delivery_rate)}
            </div>
            <div className="text-sm text-gray-600 mt-2">Success Rate</div>
            <div className="text-xs text-gray-500 mt-1">
              Messages successfully delivered
            </div>
          </div>

          {/* Issue Rate */}
          <div className="text-center p-6 border-2 border-gray-200 rounded-lg">
            <div className="text-3xl font-bold text-red-600">
              {formatPercentage(metrics.bounce_rate + metrics.unsubscribe_rate)}
            </div>
            <div className="text-sm text-gray-600 mt-2">Issue Rate</div>
            <div className="text-xs text-gray-500 mt-1">
              Bounces + Unsubscribes
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

